import {
  timeRangeOptions,
  typeOptions
} from '@/app/KeywordTracker/constants/aiModalConstants';
import type {
  ChatMessage,
  KeywordTypeValue,
  TimeRangeValue
} from '@/app/KeywordTracker/types/aiModalTypes';
import { getColor } from '@/helpers/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  ButtonGroup,
  Dropdown,
  DropdownButton,
  Spinner
} from 'react-bootstrap';
import AiModalIconActions from './AiModalIconActions';

interface AiModalToolbarProps {
  messages: ChatMessage[];
  keyword?: string;
  isFetchingAnalysis: boolean;
  isResearching: boolean;
  isDeletingHistory: boolean;
  hasKeywordHistory: boolean;
  selectedTimeRange: TimeRangeValue | null;
  selectedType: KeywordTypeValue;
  onTimeRangeChange: (value: TimeRangeValue | null) => void;
  onTypeChange: (value: KeywordTypeValue) => void;
  onReanalyze: () => void;
  onResearch: () => void;
  onDeleteHistory: () => void;
}

const AiModalToolbar = ({
  messages,
  keyword,
  isFetchingAnalysis,
  isResearching,
  isDeletingHistory,
  hasKeywordHistory,
  selectedTimeRange,
  selectedType,
  onTimeRangeChange,
  onTypeChange,
  onReanalyze,
  onResearch,
  onDeleteHistory
}: AiModalToolbarProps) => {
  return (
    <div className="p-2 border-top d-flex align-items-center justify-content-between gap-3">
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <ButtonGroup size="sm">
          <Button
            size="sm"
            variant="secondary"
            disabled={isFetchingAnalysis}
            onClick={onReanalyze}
          >
            {isFetchingAnalysis ? (
              <>
                <Spinner
                  style={{ width: '14px', height: '14px' }}
                  animation="border"
                  className="me-1"
                />
                درحال تحلیل...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon="sync-alt" className="me-1" />
                تحلیل مجدد
                <span className="fs--2 fw-light ms-1">
                  (
                  {selectedTimeRange
                    ? timeRangeOptions.find(opt => opt.value === selectedTimeRange)
                        ?.label
                    : 'همه دوره'}
                  )
                </span>
              </>
            )}
          </Button>
          <span
            style={{
              width: 1,
              backgroundColor: getColor('gray-400')
            }}
          />
          <DropdownButton
            as={ButtonGroup}
            variant="secondary"
            disabled={isFetchingAnalysis}
            size="sm"
            className="btn-reveal-trigger m-0 p-0"
            title={
              <span className="fs--1">
                <FontAwesomeIcon className="fs--2" icon="chevron-down" />
              </span>
            }
          >
            {timeRangeOptions.map(option => (
              <Dropdown.Item
                className="m-0"
                key={option.value}
                active={selectedTimeRange === option.value}
                onClick={() => onTimeRangeChange(option.value)}
              >
                {option.label}
              </Dropdown.Item>
            ))}
            <Dropdown.Divider />
            <Dropdown.Item
              active={!selectedTimeRange}
              onClick={() => onTimeRangeChange(null)}
            >
              همه دوره
            </Dropdown.Item>
          </DropdownButton>
        </ButtonGroup>

        <ButtonGroup size="sm">
          <Button
            size="sm"
            variant="secondary"
            disabled={isResearching}
            onClick={onResearch}
          >
            {isResearching ? (
              <>
                <Spinner
                  style={{ width: '14px', height: '14px' }}
                  animation="border"
                  className="me-1"
                />
                درحال تحقیق...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon="sync-alt" className="me-1" />
                تحقیق کلمه
                <span className="fs--2 fw-light ms-1">
                  (
                  {typeOptions.find(opt => opt.value === selectedType)?.label ??
                    'پیشنهادی'}
                  )
                </span>
              </>
            )}
          </Button>
          <span
            style={{
              width: 1,
              backgroundColor: getColor('gray-400')
            }}
          />
          <DropdownButton
            as={ButtonGroup}
            variant="secondary"
            disabled={isResearching}
            size="sm"
            className="btn-reveal-trigger m-0 p-0"
            title={
              <span className="fs--1">
                <FontAwesomeIcon className="fs--2" icon="chevron-down" />
              </span>
            }
          >
            {typeOptions.map(option => (
              <Dropdown.Item
                className="m-0"
                key={option.value}
                active={selectedType === option.value}
                onClick={() => onTypeChange(option.value)}
              >
                {option.label}
              </Dropdown.Item>
            ))}
          </DropdownButton>
        </ButtonGroup>
      </div>

      <AiModalIconActions
        messages={messages}
        keyword={keyword}
        isFetchingAnalysis={isFetchingAnalysis}
        isResearching={isResearching}
        isDeletingHistory={isDeletingHistory}
        hasKeywordHistory={hasKeywordHistory}
        onDeleteHistory={onDeleteHistory}
      />
    </div>
  );
};

export default AiModalToolbar;
